import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { useAddCardOrCashfreePayMutation } from '../mutations'
import { RazorpayCustomRechargeModal } from '../overview/razorpay-custom-recharge-modal'
import { useAccountDetailsQuery } from '../queries'
import { useIsCashfreeProvider, useIsRazorpayProvider } from '../use-billing-provider'
import { AddCard } from './add-card'
import { resolveAddCardGateway, resolveAddCardSurface } from './add-card-surface'
import { CashfreeCardElementsAddCard } from './cashfree-card-elements-add-card'
import { StripeEmbeddedCheckout } from './stripe-embedded-checkout'

interface AddCardModalProps {
  isOpen: boolean
  onClose: () => void
}

export const AddCardModal = ({ isOpen, onClose }: AddCardModalProps) => {
  const { isEmbeddedCardCheckoutEnabled } = useFeatureFlags()
  const isCashfree = useIsCashfreeProvider()
  const isRazorpay = useIsRazorpayProvider()
  const { data: accountDetails } = useAccountDetailsQuery()
  const { mutate: addCard, isPending } = useAddCardOrCashfreePayMutation()

  // Funnel start — fires once per open, deferred until account details resolve the
  // real gateway (the provider hooks report false until then). One call site
  // covers every render branch below, Razorpay included, and captures opens that
  // never submit. Modal stays mounted, so reset on close.
  const addCardStartedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      addCardStartedRef.current = false
      return
    }
    if (!accountDetails || addCardStartedRef.current) return
    addCardStartedRef.current = true
    track(AnalyticsEvents.ADD_CARD_STARTED, {
      gateway: resolveAddCardGateway({ isRazorpay, isCashfree }),
      is_first_card: false,
    })
  }, [isOpen, accountDetails, isCashfree, isRazorpay])

  // Razorpay has no $0 save-card endpoint, so "Add Card" is a real card payment
  // that vaults the card — handled by the Razorpay recharge modal (card-only).
  // Defensive: today all callers pre-branch on provider before rendering
  // AddCardModal, so this is a safety net if a future caller doesn't. Kept after
  // the hooks above so the early return doesn't violate the Rules of Hooks.
  const surface = resolveAddCardSurface({
    accountDetailsLoaded: !!accountDetails,
    isRazorpay,
    isCashfree,
    embeddedEnabled: isEmbeddedCardCheckoutEnabled,
  })

  if (surface === 'razorpay') {
    return <RazorpayCustomRechargeModal isOpen={isOpen} onClose={onClose} restrictToMethod="card" />
  }

  // Currency not yet known: show the spinner rather than guess a gateway. Applies to
  // the legacy branch too, which is how Stripe stayed reachable for INR.
  if (surface === 'loading') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Card</DialogTitle>
          </DialogHeader>
          <div
            className="flex items-center justify-center py-10"
            data-testid="billing-add-card-provider-loading"
          >
            <Loader2 className="size-6 animate-spin text-secondary" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // PSP-hosted (PCI-safe) add-card: the raw PAN is entered inside the gateway's own
  // surface — Stripe's embedded iframe or Cashfree's hosted modal — never our form.
  // Flag off => legacy raw-card form (the fallback below).
  if (surface === 'cashfree-embedded' || surface === 'stripe-embedded') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Card</DialogTitle>
          </DialogHeader>
          {surface === 'cashfree-embedded' ? (
            <CashfreeCardElementsAddCard onClose={onClose} />
          ) : (
            <StripeEmbeddedCheckout onClose={onClose} />
          )}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-[444px]">
        <DialogHeader>
          <DialogTitle>Add Card</DialogTitle>
        </DialogHeader>
        <AddCard onClose={onClose} addCard={addCard} isPending={isPending} />
        <DialogFooter className="flex flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            data-testid="billing-add-card-cancel-btn"
          >
            Cancel
          </Button>
          <FormSubmitButton
            formId="add-card"
            disabled={isPending}
            loading={isPending}
            data-testid="billing-add-card-submit-btn"
          >
            <span>Add Card</span>
          </FormSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
